"use client"

import React, {useEffect, useState} from "react";
import {Bar, BarChart, CartesianGrid, XAxis, YAxis, ResponsiveContainer, Tooltip, Legend, ReferenceLine} from "recharts";
import {Card, CardContent, CardHeader, CardTitle} from "@/components/ui/card";
import {ChartContainer, ChartTooltip, ChartTooltipContent} from "@/components/ui/chart";
import axiosInstance from '@/api/axios.js';
import { useTranslation } from 'react-i18next';

// Set this to true to use fake data, false for real API data
const USE_FAKE_DATA = false;

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const generateFakeData = () => {
    const yearlyTotals = {
        pmd: 78.64,
        organic: 50.11,
        paper: 7.22,
        residual: -45.0
    };

    return MONTHS.map((month, i) => {
        const monthFactor = 0.5 + (i / 11) * 1.5;
        const randomFactor = 0.9 + Math.random() * 0.2;
        const residualVariation = 0.7 + Math.random() * 0.6;
        const residualSeasonal = 1.0 + 0.3 * Math.sin((i / 12) * 2 * Math.PI);

        return {
            month,
            PMD: Math.max(0, (yearlyTotals.pmd / 12) * monthFactor * randomFactor),
            organic: Math.max(0, (yearlyTotals.organic / 12) * (0.8 + (i / 11) * 0.4) * randomFactor),
            paper: Math.max(0, (yearlyTotals.paper / 12) * (0.8 + Math.random() * 0.4)),
            residual: Math.min(0, (yearlyTotals.residual / 12) * residualVariation * residualSeasonal)
        };
    });
};

export function CostSavingsBarChart({selectedBins, selectedPeriod}) {
    const { t } = useTranslation();
    const [chartData, setChartData] = useState(null);
    const [isLoading, setIsLoading] = useState(!USE_FAKE_DATA);

    const chartConfig = {
        PMD: {
            label: t('charts.costSavings.legend.pmd'),
            color: "hsl(var(--chart-7))",
        },
        organic: {
            label: t('charts.costSavings.legend.organic'),
            color: "hsl(var(--chart-6))",
        },
        paper: {
            label: t('charts.costSavings.legend.paper'),
            color: "hsl(var(--chart-8))",
        },
        residual: {
            label: t('charts.costSavings.legend.residualCost'),
            color: "hsl(var(--chart-9))",
        }
    };

    useEffect(() => {
        if (USE_FAKE_DATA) {
            setIsLoading(false);
            return;
        }

        const fetchData = async () => {
            setIsLoading(true);
            try {
                const params = {
                    timeRange: selectedPeriod || 'Year',
                    bins: selectedBins.join(',')
                };

                const response = await axiosInstance.get('cost_savings_page', {params});
                setChartData(response.data);
            } catch (error) {
                console.error("Error fetching cost savings data:", error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchData();
    }, [selectedBins, selectedPeriod]);

    const formatChartData = (data) => {
        if (!data || !data.period_labels || !data.period_savings) {
            // Fallback to legacy format
            if (data && data.savings) {
                const periods = Object.keys(data.savings.pmd || data.savings.PMD || {});
                return periods.map(period => ({
                    month: period,
                    PMD: (data.savings.pmd && data.savings.pmd[period]) ||
                        (data.savings.PMD && data.savings.PMD[period]) || 0,
                    organic: (data.savings.organic && data.savings.organic[period]) || 0,
                    paper: (data.savings.paper && data.savings.paper[period]) || 0,
                    residual: 0
                }));
            }
            return [];
        }

        // Use period-specific residual data from backend
        return data.period_labels.map(period => {
            const periodData = data.period_savings[period];

            return {
                month: period,
                PMD: periodData.PMD || periodData.pmd || 0,
                organic: periodData.organic || 0,
                paper: periodData.paper || 0,
                residual: periodData.residual || 0 // Now comes directly from backend
            };
        });
    };

    const processedData = USE_FAKE_DATA ? generateFakeData() : (chartData ? formatChartData(chartData) : []);

    // Y-axis self-adjustment logic similar to waste stream production
    const getYAxisDomain = (data) => {
        if (!data || data.length === 0) return [0, 'auto'];

        // Get all positive and negative values
        const allValues = data.flatMap(item => [
            item.PMD || 0,
            item.organic || 0,
            item.paper || 0,
            item.residual || 0
        ]).filter(val => val !== 0);

        if (allValues.length === 0) return [0, 'auto'];

        const minValue = Math.min(...allValues);
        const maxValue = Math.max(...allValues);
        const maxAbsValue = Math.max(Math.abs(minValue), Math.abs(maxValue));

        // If all values are below €1, adjust domain for better visibility
        if (maxAbsValue < 1) {
            // Handle edge case: ensure minimum range of 0.01 to 0.99
            const adjustedMin = minValue < 0 ? Math.min(minValue - 0.01, -0.01) : 0.01;
            const adjustedMax = Math.max(maxValue + 0.01, 0.99);

            return [adjustedMin, adjustedMax];
        }

        // For values >= €1, use auto scaling
        return ['auto', 'auto'];
    };

    const yAxisDomain = getYAxisDomain(processedData);
    const shouldShowPreciseValues = yAxisDomain[0] !== 'auto' && yAxisDomain[1] !== 'auto';

    const getTranslatedLabel = (dataKey) => {
        const labelMap = {
            'PMD': t('charts.costSavings.legend.pmd'),
            'organic': t('charts.costSavings.legend.organic'),
            'paper': t('charts.costSavings.legend.paper'),
            'residual': t('charts.costSavings.legend.residualCost')
        };
        return labelMap[dataKey] || dataKey;
    };

    const CustomTooltip = ({active, payload, label}) => {
        if (active && payload && payload.length) {
            return (
                <div className="rounded-lg border bg-background p-2 shadow-sm">
                    <div className="grid grid-cols-2 gap-2">
                        <div className="flex flex-col">
                            <span className="text-[0.70rem] uppercase text-muted-foreground">
                                {label}
                            </span>
                        </div>
                    </div>
                    <div className="grid gap-1">
                        {payload.map((entry, index) => (
                            <div key={index} className="flex items-center gap-2">
                                <div
                                    className="h-2 w-2 rounded-full"
                                    style={{backgroundColor: entry.color}}
                                />
                                <span className="text-sm text-muted-foreground">
                                    {getTranslatedLabel(entry.dataKey)}:
                                </span>
                                <span className={`text-sm font-medium ${entry.value < 0 ? 'text-destructive' : ''}`}>
                                    {entry.value < 0 ? '-' : ''}€{Math.abs(entry.value).toFixed(
                                        shouldShowPreciseValues ? 3 : 2
                                    )}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            );
        }
        return null;
    };

    // Calculate summary statistics for debugging/display
    const totalSavings = processedData.reduce((sum, item) =>
        sum + item.PMD + item.organic + item.paper, 0
    );
    const totalResidualCost = processedData.reduce((sum, item) =>
        sum + item.residual, 0
    );
    const netSavings = totalSavings + totalResidualCost;

    return (
        <div className="sm:rounded-lg bg-muted/50 p-4 h-full flex flex-col">
            <div className="flex justify-between items-center mb-2">
                <h2 className="scroll-m-20 text-xl font-semibold tracking-tight">
                    {t('charts.costSavings.title')}
                </h2>
                {!USE_FAKE_DATA && chartData && (
                    <div className="text-sm text-muted-foreground">
                        {t('charts.costSavings.net')}: €{netSavings.toFixed(shouldShowPreciseValues ? 3 : 2)}
                    </div>
                )}
            </div>
            <div className="flex-grow w-full">
                {isLoading ? (
                    <div className="w-full h-full flex items-center justify-center">
                        {t('common.loading')}
                    </div>
                ) : (
                    <ChartContainer
                        config={chartConfig}
                        className="h-full w-full"
                    >
                        <BarChart
                            data={processedData}
                            margin={{
                                top: 20,
                                right: 30,
                                left: 20,
                                bottom: 5,
                            }}
                            barCategoryGap="20%"
                        >
                            <CartesianGrid
                                strokeDasharray="3 3"
                                stroke="hsl(var(--border))"
                                vertical={false}
                            />
                            <XAxis
                                dataKey="month"
                                tick={{fontSize: 12}}
                                tickLine={{stroke: 'hsl(var(--border))'}}
                                axisLine={{stroke: 'hsl(var(--border))'}}
                                type="category"
                            />
                            <YAxis
                                domain={yAxisDomain}
                                tick={{fontSize: 12}}
                                tickLine={{stroke: 'hsl(var(--border))'}}
                                axisLine={{stroke: 'hsl(var(--border))'}}
                                tickFormatter={(value) => {
                                    const precision = shouldShowPreciseValues ? 3 : 0;
                                    return `€${value.toFixed(precision)}`;
                                }}
                                width={70}
                            />
                            <ReferenceLine y={0} stroke="hsl(var(--border))" strokeWidth={2} />
                            <ChartTooltip content={<CustomTooltip/>}/>

                            {/* Positive savings bars */}
                            <Bar
                                dataKey="PMD"
                                stackId="positive"
                                fill="var(--color-PMD)"
                                stroke="white"
                                strokeWidth={1}
                                maxBarSize={60}
                            />
                            <Bar
                                dataKey="organic"
                                stackId="positive"
                                fill="var(--color-organic)"
                                stroke="white"
                                strokeWidth={1}
                                maxBarSize={60}
                            />
                            <Bar
                                dataKey="paper"
                                stackId="positive"
                                fill="var(--color-paper)"
                                stroke="white"
                                strokeWidth={1}
                                maxBarSize={60}
                            />

                            {/* Negative residual bars */}
                            <Bar
                                dataKey="residual"
                                stackId="negative"
                                fill="var(--color-residual)"
                                stroke="white"
                                strokeWidth={1}
                                maxBarSize={60}
                            />
                        </BarChart>
                    </ChartContainer>
                )}
            </div>
        </div>
    );
}

export default CostSavingsBarChart;